import React, { useEffect, useState } from "react";
import { FileCheck, ExternalLink } from "lucide-react";
import { api, fileUrl } from "@/lib/apiClient";
import { PageHeader, Loader, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

export default function AdminReviews() {
  const [subs, setSubs] = useState(null);
  const [status, setStatus] = useState("under_review");
  const [feedback, setFeedback] = useState({});

  const load = async () => { const { data } = await api.get(`/admin/reviews?status=${status}`); setSubs(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const act = async (sid, action) => {
    await api.post(`/admin/reviews/${sid}`, { action, feedback: feedback[sid] || "" });
    toast.success(action === "approved" ? "Approved" : "Revision requested");
    load();
  };

  return (
    <div>
      <PageHeader title="Evidence Reviews" subtitle="Approve or request revisions on student submissions"
        action={
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44" data-testid="review-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="under_review">Under Review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="needs_revision">Needs Revision</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>} />

      {!subs ? <Loader /> : subs.length === 0 ? (
        <EmptyState icon={FileCheck} title="Nothing to review" description="No submissions with this status." testId="reviews-empty" />
      ) : (
        <div className="space-y-4">
          {subs.map((s) => (
            <div key={s.id} className="bg-white border border-border rounded-2xl p-5 shadow-sm" data-testid={`review-${s.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  {s.student_photo ? <img src={fileUrl(s.student_photo)} className="w-10 h-10 rounded-full object-cover" alt="" /> : <div className="w-10 h-10 rounded-full bg-teal/10 text-teal flex items-center justify-center font-bold">{s.student_name?.[0]}</div>}
                  <div>
                    <p className="font-medium">{s.student_name}</p>
                    <p className="text-xs text-muted-foreground">{s.student_email} · {s.phase_title}</p>
                  </div>
                </div>
                <StatusBadge status={s.status} />
              </div>
              {s.note && <p className="text-sm text-muted-foreground mt-3">Note: {s.note}</p>}
              {s.file_url && (
                <a href={`${fileUrl(s.file_url, true)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-teal text-sm font-medium mt-3 hover:underline" data-testid={`view-evidence-${s.id}`}>
                  View screenshot <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              {s.status === "under_review" && (
                <div className="mt-4 space-y-3">
                  <Textarea placeholder="Feedback (required for revision)" value={feedback[s.id] || ""} onChange={(e) => setFeedback((f) => ({ ...f, [s.id]: e.target.value }))} data-testid={`feedback-${s.id}`} />
                  <div className="flex gap-2">
                    <Button className="rounded-full bg-brandgreen hover:bg-brandgreen/90" onClick={() => act(s.id, "approved")} data-testid={`approve-${s.id}`}>Approve</Button>
                    <Button variant="outline" className="rounded-full" onClick={() => act(s.id, "needs_revision")} data-testid={`revise-${s.id}`}>Request Revision</Button>
                  </div>
                </div>
              )}
              {s.feedback && s.status !== "under_review" && <p className="text-sm mt-3 p-3 rounded-lg bg-secondary/50"><b>Feedback:</b> {s.feedback}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
